import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Percent, Plus, Pencil, Trash2, Loader2, CalendarRange, History } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { DateField } from "@/components/DateField";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = { name: "", pct: "50", start_date: "", end_date: "" };
const todayCl = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Santiago" });

function status(c) {
  const t = todayCl();
  if (t < c.start_date) return { label: "Programado", cls: "bg-sky-100 text-sky-800" };
  if (t > c.end_date) return { label: "Finalizado", cls: "bg-muted text-muted-foreground" };
  return { label: "Vigente", cls: "bg-emerald-100 text-emerald-800" };
}

function CampaignForm({ initial, onCancel, onSaved }) {
  const [f, setF] = useState(initial ? { ...initial, pct: String(initial.pct) } : EMPTY);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.name.trim() || !f.start_date || !f.end_date) { toast.error("Completa nombre, fecha de inicio y fecha de fin"); return; }
    setBusy(true);
    try {
      const body = { name: f.name, pct: Number(f.pct), start_date: f.start_date, end_date: f.end_date };
      const { data } = initial ? await api.put(`/admin/discount-campaigns/${initial.id}`, body) : await api.post("/admin/discount-campaigns", body);
      toast.success(initial ? "Período actualizado" : "Período creado"); onSaved(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border p-4 space-y-3 bg-muted/30" data-testid="campaign-form">
      <div><Label>Nombre del período</Label>
        <Input value={f.name} maxLength={40} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="ej. Lanzamiento" data-testid="campaign-name-input" /></div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div><Label>Desde</Label><DateField value={f.start_date} max={f.end_date || undefined} onChange={(v) => setF({ ...f, start_date: v })} testId="campaign-start-input" floating /></div>
        <div><Label>Hasta</Label><DateField value={f.end_date} min={f.start_date || undefined} onChange={(v) => setF({ ...f, end_date: v })} testId="campaign-end-input" floating /></div>
        <div><Label>Descuento</Label>
          <Select value={f.pct} onValueChange={(v) => setF({ ...f, pct: v })}>
            <SelectTrigger data-testid="campaign-pct-select"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-60">
              {Array.from({ length: 20 }, (_, i) => (i + 1) * 5).map((v) => <SelectItem key={v} value={String(v)}>{v}%</SelectItem>)}
            </SelectContent>
          </Select></div>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel} data-testid="campaign-cancel-btn">Cancelar</Button>
        <Button size="sm" onClick={save} disabled={busy} data-testid="campaign-save-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar período"}</Button>
      </div>
    </div>
  );
}

function CampaignRow({ c, onEdit, onDelete }) {
  const s = status(c);
  return (
    <div className="flex items-center gap-3 rounded-xl border p-3" data-testid={`campaign-item-${c.id}`}>
      <div className="min-w-0 flex-1">
        <div className="font-semibold flex items-center gap-2 flex-wrap">{c.name}
          <Badge variant="outline" className="gap-1"><Percent className="w-3 h-3" />{c.pct}</Badge>
          <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${s.cls}`} data-testid={`campaign-status-${c.id}`}>{s.label}</span>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">{formatDate(`${c.start_date}T12:00:00`)} – {formatDate(`${c.end_date}T12:00:00`)} · {c.businesses_count} Maestro(s)</p>
      </div>
      <Button variant="ghost" size="icon" onClick={onEdit} aria-label="Editar período" data-testid={`campaign-edit-${c.id}`}><Pencil className="w-4 h-4" /></Button>
      <Button variant="ghost" size="icon" onClick={onDelete} aria-label="Eliminar período" className="text-destructive" data-testid={`campaign-delete-${c.id}`}><Trash2 className="w-4 h-4" /></Button>
    </div>
  );
}

function CampaignHistory({ items }) {
  return (
    <div className="pt-2 border-t" data-testid="campaign-history">
      <p className="font-semibold text-sm mt-3 mb-2 flex items-center gap-1.5"><History className="w-4 h-4 text-primary" />Historial de descuentos</p>
      {items.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="campaign-history-empty">Aún no hay períodos terminados.</p> : (
        <div className="divide-y rounded-xl border">
          {items.map((c) => (
            <div key={c.id} className="flex items-center gap-3 px-3 py-2.5" data-testid={`campaign-history-${c.id}`}>
              <div className="min-w-0 flex-1">
                <p className="font-medium text-sm flex items-center gap-2 flex-wrap">{c.name}<Badge variant="outline" className="gap-1"><Percent className="w-3 h-3" />{c.pct}</Badge></p>
                <p className="text-xs text-muted-foreground">{formatDate(`${c.start_date}T12:00:00`)} – {formatDate(`${c.end_date}T12:00:00`)}</p>
              </div>
              <div className="text-right shrink-0" data-testid={`campaign-history-count-${c.id}`}>
                <p className="font-bold">{c.businesses_count}</p>
                <p className="text-[11px] text-muted-foreground">Maestro{c.businesses_count === 1 ? "" : "s"}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function DiscountCampaigns() {
  const [list, setList] = useState(null);
  const [editing, setEditing] = useState(null);
  useEffect(() => { api.get("/admin/discount-campaigns").then((r) => setList(r.data)); }, []);
  const saved = (data) => { setList(data); setEditing(null); };
  const t = todayCl();
  const open = (list || []).filter((c) => c.end_date >= t);
  const done = (list || []).filter((c) => c.end_date < t).sort((a, b) => b.end_date.localeCompare(a.end_date));
  const remove = async (c) => {
    if (!window.confirm(`¿Eliminar el período «${c.name}»? Los Maestros que ya lo recibieron lo mantienen hasta su fecha de fin.`)) return;
    try { saved((await api.delete(`/admin/discount-campaigns/${c.id}`)).data); toast.success("Período eliminado"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Card className="p-6 space-y-4 mt-6" data-testid="discount-campaigns-card">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2"><CalendarRange className="w-5 h-5 text-primary" /><h3 className="font-bold">Descuento para nuevos Maestros</h3></div>
        {editing === null && <Button size="sm" variant="outline" onClick={() => setEditing({})} data-testid="campaign-add-btn"><Plus className="w-4 h-4 mr-1" />Agregar</Button>}
      </div>
      <p className="text-sm text-muted-foreground">Los usuarios que crean su cuenta dentro de un período reciben ese descuento en el cobro de publicación. Al terminar el período, el descuento se quita automáticamente. Los períodos que se crean no pueden cruzarse con otros periodos ya existentes.</p>
      {editing && <CampaignForm initial={editing.id ? editing : null} onCancel={() => setEditing(null)} onSaved={saved} />}
      {!list ? <Loader2 className="w-5 h-5 animate-spin text-primary" /> : (<>
        {open.length === 0
          ? <p className="text-sm text-muted-foreground" data-testid="campaigns-empty">No hay períodos vigentes ni programados.</p>
          : <div className="space-y-2">{open.map((c) => <CampaignRow key={c.id} c={c} onEdit={() => setEditing(c)} onDelete={() => remove(c)} />)}</div>}
        <CampaignHistory items={done} />
      </>)}
    </Card>
  );
}
